import { v4 as uuidv4 } from 'uuid';
import { CHECKBOX, MATRICE, RADIO, TABLE } from '../constants';
import { BlockProps, ChapterProps, ColumnProps, QuestionAnswer, RowProps, TabularAnswer } from '../models/global';
import { CodeInterface } from '../models/state';

export const generateRandomString = (isUUID: boolean, length?: number) => {
  let text = '';
  if (isUUID) {
    text = uuidv4();
  } else {
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

    for (let i = 0; i < length; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
  }
  return text;
};

export const validateSurveyForm = (chapters: ChapterProps[], language: string) => {
  const invalidFields = [];
  chapters.forEach(chapter => {
    if (!chapter[language === 'English' ? 'name' : `name${language}`]) {
      invalidFields.push(`CHAPTER_${chapter.id}`);
    }
    chapter.sections.forEach(section => {
      if (!section[language === 'English' ? 'name' : `name${language}`]) {
        invalidFields.push(`SECTION_${section.id}`);
      }
      section.fieldBlocks.forEach(block => {
        if (
          !block[language === 'English' ? 'question' : `question${language}`] &&
          ![TABLE, MATRICE].includes(block.fieldType)
        ) {
          invalidFields.push(`QUESTION_${block.id}`);
        }
        if (!block.fieldType) {
          invalidFields.push(`FIELD_TYPE_${block.id}`);
        }

        if (!block.codeId && ![MATRICE].includes(block.fieldType)) {
          invalidFields.push(`CODE_${block.id}`);
        }

        if ([CHECKBOX, RADIO].includes(block.fieldType)) {
          block.options.forEach((option, optionIndex) => {
            if (!option[language === 'English' ? 'name' : `name${language}`]) {
              invalidFields.push(`OPTION_${optionIndex}_${block.id}`);
            }
          });
        }

        if (block.table) {
          if (!block.table.columns?.length) {
            invalidFields.push(`COLUMN_LENGTH_${block.table.id}_${block.id}`);
          } else {
            block.table.columns.forEach((column, columnIndex) => {
              if (!column[language === 'English' ? 'name' : `name${language}`]) {
                invalidFields.push(`COLUMN_${columnIndex}_${block.table.id}_${block.id}`);
              }
            });
          }
        }

        if (block.matrice) {
          if (!block.matrice.columns?.length) {
            invalidFields.push(`COLUMN_LENGTH_${block.matrice.id}_${block.id}`);
          } else {
            block.matrice.columns.forEach((column, columnIndex) => {
              if (!column[language === 'English' ? 'name' : `name${language}`]) {
                invalidFields.push(`COLUMN_${columnIndex}_${block.matrice.id}_${block.id}`);
              }
            });
            block.matrice.rows.forEach((row, rowIndex) => {
              if (!row[language === 'English' ? 'name' : `name${language}`]) {
                invalidFields.push(`ROW_${rowIndex}_${block.matrice.id}_${block.id}`);
              }
              if (!row['codeId']) {
                invalidFields.push(`CODE_${rowIndex}_${block.matrice.id}_${block.id}`);
              }
            });
          }
        }
      });
    });
  });
  return invalidFields;
};

export const validateUserSurveyAnswers = (
  chapters: ChapterProps[],
  questionAnswers: QuestionAnswer[],
  tabularAnswers: TabularAnswer[],
) => {
  const invalidFields = [];
  let questionCount = 0;
  chapters.forEach(chapter => {
    chapter.sections.forEach(section => {
      const shownQuestions = section.fieldBlocks.filter(item => !item.hide);
      questionCount += shownQuestions.length;

      shownQuestions.forEach(block => {
        let answer = null;
        if ([TABLE, MATRICE].includes(block.fieldType)) {
          answer = tabularAnswers.find(tabularAnswerItem => tabularAnswerItem.sectionId === section.id);
        } else {
          answer = questionAnswers.find(questionAnswerItem => questionAnswerItem.questionId === block.id);
        }

        if (!answer) {
          invalidFields.push(`QUESTION_${block.id}`);
        }
      });
    });
  });
  const emptyFieldsPercentage = (invalidFields.length * 100) / questionCount;
  return {
    invalidFields,
    filledPercentage: 100 - emptyFieldsPercentage,
  };
};

export const isInViewport = el => {
  if (!el) {
    return false;
  }
  const rect = el.getBoundingClientRect();
  const till = window.innerHeight || document.documentElement.clientHeight;
  const isinview = rect.top >= 0 && rect.bottom <= till - 100;

  return isinview;
};

export const handleActiveSection = (chapters: ChapterProps[]) => {
  const activeChapter = chapters.find(el => el.active);
  if (!activeChapter) {
    return;
  }
  const sections = [];
  activeChapter.sections.map(section => {
    sections.push(document.getElementById(section.id));
  });
  sections.forEach(section => {
    if (isInViewport(section)) {
      const activeSection = activeChapter.sections.find(el => el.active);

      const currentSection = activeChapter.sections.find(el => el.id === section.id);
      if (activeSection) {
        activeSection.active = false;
      }
      if (currentSection) {
        currentSection.active = true;
      }
    }
  });
  return chapters;
};

export const handleOnScroll = (chapterId: string, chapters: ChapterProps[], sectionId?: string) => {
  let id = chapterId;
  const chapter = chapters.find(el => el.id === chapterId);
  if (!chapter?.active) {
    const chapterActive = chapters.find(el => el.active);
    if (chapterActive) {
      chapterActive.active = false;
      chapterActive.sections.forEach(section => {
        section.active = false;
      });
    }
    chapter.active = true;
  }

  if (sectionId) {
    id = sectionId;
    const sectionActive = chapter.sections.find(section => section.active);
    const section = chapter.sections.find(section => section.id === sectionId);
    if (sectionActive) {
      sectionActive.active = false;
    }
    if (section) {
      section.active = true;
    }
  }

  return { idToScrollTo: id, chapters };
};

export const getSurveyItem = (chapters: ChapterProps[], chapterId: string, sectionId?: string, blockId?: string) => {
  const chapter = chapters.find(el => el.id === chapterId);
  if (chapter && sectionId) {
    const section = chapter.sections.find(el => el.id === sectionId);
    if (section && blockId) {
      return section.fieldBlocks.find(el => el.id === blockId);
    }
    return section;
  }
  return chapter;
};

export const swapColumns = (data: ColumnProps[], newItem: ColumnProps) => {
  data.push(data[data.length - 1]);
  if (data[data.length - 1].name === `Col ${data.length - 1}`) {
    data[data.length - 1].name = `Col ${data.length}`;
  }
  newItem.name = `Col ${data.length - 1}`;
  data[data.length - 2] = newItem;
};

export const validFieldBlockForSwap = (
  current: BlockProps,
  targetData: BlockProps,
  index: number,
  direction: 'prev' | 'next',
  tillOrder?: number,
) => {
  const optionIds = targetData.dependsOnOptions || targetData.interrelatedFields?.map(el => el.optionId);
  const determines = current.options?.find(option => optionIds?.includes(option.id));

  if (determines) {
    return;
  }

  if (direction === 'prev' && !isNaN(tillOrder) && (current.order || index + 1) - 1 <= tillOrder) {
    return;
  }

  return true;
};

export const swapItems = (
  data: any[],
  index: number,
  direction: 'prev' | 'next',
  tillOrder?: number,
  type?: string,
) => {
  const target = direction === 'next' ? index + 1 : index - 1;
  const current = data[index];

  if (type === 'fieldBlock' && !validFieldBlockForSwap(current, data[target], index, direction, tillOrder)) {
    return;
  }

  data[index] = data[target];
  if (data[index].order) {
    if (direction === 'next') {
      data[index].order--;
    } else {
      data[index].order++;
    }
  }

  data[target] = current;
  if (data[target].order) {
    if (direction === 'next') {
      data[target].order++;
    } else {
      data[target].order--;
    }
  }

  return true;
};

export const resetBlockQuestion = block => {
  block.question = '';
  block.questionArabic = '';
  block.questionChinese = '';
  block.questionFrench = '';
  block.questionRussian = '';
  block.questionSpanish = '';
};

export const compareData = (data1, data2) => {
  return JSON.stringify(data1) === JSON.stringify(data2);
};

export const sanitizeChapters = chapters => {
  return chapters.map(chapter => {
    delete chapter.active;
    chapter.sections.forEach(section => {
      delete section.active;
    });
    return chapter;
  });
};

export const getFilteredCodes = (chapters: ChapterProps[], codes: CodeInterface[]) => {
  const codesUsed = [];
  chapters.forEach(chapter => {
    chapter.sections.forEach(section => {
      section.fieldBlocks.forEach(fieldBlock => {
        codesUsed.push(fieldBlock.codeId);
        if (fieldBlock.fieldType === MATRICE && fieldBlock?.matrice?.rows?.length) {
          (fieldBlock.matrice.rows as RowProps[]).forEach(row => {
            codesUsed.push(row.codeId);
          });
        }
      });
    });
  });
  return codes.filter(code => !codesUsed.includes(code.codeId));
};
